export type LoginStep = "signIn" | "totp" | "setup" | "newPassword" | "recover" | "resetPassword";
export type AuthStep =
  | { step: "done" }
  | { step: "totp" }
  | { step: "setup"; setupUri: string; setupKey: string }
  | { step: "newPassword"; missingAttributes: string[] }
  | { step: "resetRequired" };
export interface Session { accessToken: string; email: string; }
export interface AuthDriver {
  getSession(): Promise<Session | null>;
  signIn(email: string, password: string): Promise<AuthStep>;
  confirm(response: string, attributes?: Record<string, string>): Promise<AuthStep>;
  resetPassword(email: string): Promise<void>;
  confirmReset(email: string, code: string, password: string): Promise<void>;
  signOut(): Promise<void>;
}
export interface AuthState {
  status: "loading" | "signedOut" | "signedIn";
  step: LoginStep;
  busy: boolean;
  username: string;
  email: string;
  error: string;
  notice: string;
  setupUri: string;
  setupKey: string;
  missingAttributes: string[];
}
const initialState = (): AuthState => ({
  status: "signedOut", step: "signIn", busy: false, username: "", email: "",
  error: "", notice: "", setupUri: "", setupKey: "", missingAttributes: [],
});

/** Only accept local CMS destinations, including destinations supplied by old bookmarks. */
export function safeReturnTo(value: unknown): string {
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//") || /[\\\u0000-\u0020]/.test(value)) return "/";
  try {
    const url = new URL(value, "https://admin.everandupbooks.com");
    if (url.origin !== "https://admin.everandupbooks.com" || /^\/(login|callback)(\/|$)/.test(url.pathname)) return "/";
    return url.pathname + url.search + url.hash;
  } catch { return "/"; }
}

export function authErrorMessage(error: unknown): string {
  const name = error instanceof Error ? error.name : "";
  switch (name) {
    case "NotAuthorizedException": case "UserNotFoundException":
      return "We couldn't sign you in. Check your details or start again.";
    case "CodeMismatchException": case "VerifySoftwareTokenException":
      return "That code wasn't accepted. Check your authenticator and try the current code.";
    case "ExpiredCodeException":
      return "That code has expired. Request a new code and try again.";
    case "InvalidPasswordException": case "PasswordHistoryPolicyViolationException":
      return "Choose a new password with at least 12 characters, including uppercase and lowercase letters, a number and a symbol.";
    case "LimitExceededException": case "TooManyRequestsException":
      return "Too many attempts. Please wait a little before trying again.";
    case "InvalidParameterException":
      return "This account couldn't complete that step. Check your details or ask your account administrator for help.";
    case "UserNotConfirmedException":
      return "Your account needs administrator assistance before you can sign in.";
    case "SignInException":
      return "Your sign-in attempt has expired. Go back and sign in again.";
    case "NetworkError": case "TypeError":
      return "We couldn't connect. Check your connection and try again.";
    case "UnsupportedAuthStep":
      return "This account needs a different sign-in step. Please ask your account administrator for help.";
    default: return "We couldn't complete that step. Please try again.";
  }
}

/** Authentication orchestration is separate from the UI and never stores passwords or codes. */
export class AuthController {
  private state: AuthState = { ...initialState(), status: "loading" };
  private listeners = new Set<() => void>();
  private initialization: Promise<void> | undefined;
  private generation = 0;
  constructor(private driver: AuthDriver) {}
  getSnapshot = (): AuthState => this.state;
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  private update(patch: Partial<AuthState>) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((listener) => listener());
  }
  initialize = (): Promise<void> => {
    this.initialization ??= this.restore();
    return this.initialization;
  };
  private async restore() {
    const generation = this.generation;
    try {
      const session = await this.driver.getSession();
      if (generation === this.generation) this.update({ status: session ? "signedIn" : "signedOut", email: session?.email ?? "" });
    } catch {
      if (generation === this.generation) await this.signOut("Your session couldn't be restored. Please sign in again.");
    }
  }
  private async run(operation: () => Promise<void>) {
    if (this.state.busy) return;
    const generation = this.generation;
    this.update({ busy: true, error: "", notice: "" });
    try { await operation(); }
    catch (error) {
      if (generation === this.generation) this.update({ error:
        error instanceof Error && error.name === "CodeMismatchException" && this.state.step === "resetPassword"
          ? "That code wasn't accepted. Check the email code or request a new one."
          : authErrorMessage(error),
      });
    }
    finally { if (generation === this.generation) this.update({ busy: false }); }
  }
  private async advance(next: AuthStep, generation: number) {
    if (generation !== this.generation) return;
    const cleared = { setupUri: "", setupKey: "", missingAttributes: [] as string[] };
    if (next.step === "done") {
      const session = await this.driver.getSession();
      if (generation !== this.generation) return;
      if (!session) throw new Error("Authenticated session missing");
      this.update({ ...cleared, status: "signedIn", step: "signIn", email: session.email, username: "", busy: false });
    } else if (next.step === "resetRequired") {
      this.update({ ...cleared, step: "recover" });
      await this.driver.resetPassword(this.state.username);
      if (generation === this.generation) this.update({ step: "resetPassword" });
    } else {
      this.update({ ...cleared, ...next });
    }
  }
  signIn = async (email: string, password: string) => {
    if (this.state.busy || this.state.status === "signedIn") return;
    this.update({ username: email.trim(), setupUri: "", setupKey: "", missingAttributes: [] });
    const generation = this.generation;
    await this.run(async () => this.advance(await this.driver.signIn(email.trim(), password), generation));
  };
  confirm = async (response: string, email?: string) => {
    const generation = this.generation;
    await this.run(async () => {
      const attributes: Record<string, string> = {};
      if (this.state.missingAttributes.includes("email")) attributes.email = email?.trim() || this.state.username;
      if (this.state.missingAttributes.some((attribute) => attribute !== "email")) {
        throw Object.assign(new Error(), { name: "UnsupportedAuthStep" });
      }
      await this.advance(await this.driver.confirm(response, attributes), generation);
    });
  };
  recover = async (email: string) => {
    if (this.state.busy) return;
    const generation = this.generation;
    this.update({ username: email.trim() });
    await this.run(async () => {
      await this.driver.resetPassword(email.trim());
      if (generation === this.generation) this.update({ step: "resetPassword" });
    });
  };
  confirmReset = async (code: string, password: string) => {
    const generation = this.generation;
    await this.run(async () => {
      await this.driver.confirmReset(this.state.username, code, password);
      if (generation === this.generation) this.update({ ...initialState(), notice: "Your password has been updated. Sign in with your new password." });
    });
  };
  showRecovery = () => {
    if (!this.state.busy) this.update({ step: "recover", error: "", notice: "", setupUri: "", setupKey: "", missingAttributes: [] });
  };
  restart = async () => {
    if (!this.state.busy) await this.signOut();
  };
  signOut = async (notice = "") => {
    const generation = ++this.generation;
    const wasSignedIn = this.state.status === "signedIn";
    this.update({ ...initialState(), status: wasSignedIn ? "signedIn" : "signedOut", email: this.state.email, notice, busy: true });
    try { await this.driver.signOut(); } catch { /* Local credentials are cleared by the driver even offline. */ }
    finally { if (generation === this.generation) this.update({ ...initialState(), notice }); }
  };
  getAccessToken = async (): Promise<string> => {
    if (this.state.status !== "signedIn" || this.state.busy) throw new Error("Please sign in to continue.");
    const generation = this.generation;
    try {
      const session = await this.driver.getSession();
      if (generation !== this.generation) throw new Error("Session changed");
      if (!session) throw new Error("Session expired");
      return session.accessToken;
    } catch {
      if (generation === this.generation) await this.signOut("Your session has expired. Please sign in again.");
      throw new Error("Your session has expired. Please sign in again.");
    }
  };
  onUnauthorized = () => { void this.signOut("Your session has expired. Please sign in again."); };
}
